import React, { useState } from 'react';
import { Alert, Button, InputNumber, Select, Space, Typography } from 'antd';
import { CloseOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { formatLocalizedQuantity } from '../utils/numberFormatting';
import { formatUnit } from '../utils/units';
import { POLISH_DECIMAL_INPUT_NUMBER_PROPS, TRAILER_PAYLOAD_KG } from '../utils/transportCostAllocation';
import {
  convertLineAmount,
  isExplicit,
  isOverOrdered,
  resolveSupplierCargoLines,
  setValue,
  supplierCargoWeight,
  type SupplierCargoEntry,
  type SupplierCargoLine,
  type SupplierCargoUnit,
} from '../modules/transports/supplierTruckCargo';
import './TransportSupplierCargoEditor.css';
import { singleFinalDot } from '../utils/sentenceEnd';

const { Text } = Typography;

export interface SupplierCargoGroup {
  supplierOrderId: string;
  label: string;
  /** Owner 2026-10-10: the ZD header — supplier and loading place (optional). */
  supplierName?: string | null;
  loadingPlace?: string | null;
  /** ZD status tag in the band (read-only view in „Co jedzie”). */
  statusTag?: React.ReactNode;
  lines: SupplierCargoLine[];
  /** Other trucks carrying the rest of this ZD (for the warning); empty when none / unknown. */
  otherTruckLabels: string[];
  /** The other trucks could not be read: their quantities are unknown. */
  contextUnavailable: boolean;
  loading: boolean;
}

interface Props {
  groups: SupplierCargoGroup[];
  entries: Record<string, SupplierCargoEntry>;
  onChange: (itemId: string, entry: SupplierCargoEntry) => void;
  /**
   * „Nic”: take the line off this truck (supplierTruckCargo.excludeSupplierCargoLine).
   * Returns false when it cannot (the only line of the ZD left on the truck).
   */
  onExclude?: (itemId: string) => boolean;
  /**
   * PR3: lines whose quantity is the default of a factory → customer truck
   * (Σ paired customer lines, `cargoPairing.ts`), not typed by the operator.
   */
  derivedItemIds?: ReadonlySet<string>;
  /**
   * Owner 2026-10-10: the same view in „Co jedzie” without the switch — the
   * amounts this truck carries; editing happens in the popup.
   */
  readOnly?: boolean;
  /** ✕ in the ZD band (read-only view): take the ZD off the truck. */
  onRemoveGroup?: (supplierOrderId: string) => void;
}

type LineMode = 'all' | 'part' | 'none';
const MODES: LineMode[] = ['all', 'part', 'none'];
const SWATCHES = 8;
const NO_DERIVED: ReadonlySet<string> = new Set();
const TOLERANCE = 0.0005;

/**
 * Addendum A + owner 2026-10-10 („Wszystko / Część / Nic” and the trailer):
 * per ZD line one decision — the whole rest, part of it (the only case with a
 * number) or nothing — and above the ZD tables the trailer filled by catalog
 * weight (display only, limit TRAILER_PAYLOAD_KG). Rules stay in
 * supplierTruckCargo.ts: nothing entered = the whole rest; once a line of a
 * ZD is entered, its lines without an entry are not on this truck. More than
 * ordered goes through the audited confirmation on save — nothing is blocked.
 */
const TransportSupplierCargoEditor: React.FC<Props> = ({
  groups, entries, onChange, onExclude, derivedItemIds = NO_DERIVED, readOnly = false, onRemoveGroup,
}) => {
  const tid = readOnly ? 'transport-supplier-load' : 'transport-supplier-cargo';
  const { t, i18n } = useTranslation(['transport']);
  const [partOpen, setPartOpen] = useState<ReadonlySet<string>>(new Set());
  const [blocked, setBlocked] = useState<string | null>(null);
  if (groups.length === 0) return null;
  const allLines = groups.flatMap((group) => group.lines);
  const states = resolveSupplierCargoLines(allLines, entries);
  const swatchOf = new Map(allLines.map((line, index) => [line.itemId, index % SWATCHES]));

  const quantity = (value: number | null, unit: string | null) => (
    value === null || unit === null
      ? t('transport:supplierCargo.unknown')
      : `${formatLocalizedQuantity(value, i18n.language)} ${formatUnit(unit, i18n.language)}`
  );
  const kg = (value: number) => `${formatLocalizedQuantity(Math.round(value), i18n.language)} kg`;

  // The trailer: catalog weight of one piece × pieces this truck carries.
  const { byLine: weights, kg: totalKg, withoutWeight } = supplierCargoWeight(allLines, entries);
  const scale = Math.max(TRAILER_PAYLOAD_KG, totalKg);

  const setMode = (group: SupplierCargoGroup, line: SupplierCargoLine, mode: LineMode, available: number | null, carried: number | null) => {
    setBlocked(null);
    const unit = line.unit;
    if (!unit) return;
    if (mode === 'part') {
      setPartOpen((previous) => new Set(previous).add(line.itemId));
      if (!isExplicit(line, entries[line.itemId])) {
        const start = carried && carried > 0 ? carried : available;
        if (start !== null && start > 0) onChange(line.itemId, { quantity: start, unit });
      }
      return;
    }
    setPartOpen((previous) => { const next = new Set(previous); next.delete(line.itemId); return next; });
    if (mode === 'all') {
      // The only entered line of its ZD: back to the default (the whole rest,
      // nothing sent); otherwise the ZD stays listed and this line takes all.
      // A line on its customers' sum (derived) has no such default — clearing
      // it brings the sum back — so it takes the whole rest explicitly; what
      // the customers do not take goes to the warehouse (follow-up v3.16).
      const othersEntered = group.lines.some((sibling) => sibling.itemId !== line.itemId && isExplicit(sibling, entries[sibling.itemId]));
      if (!othersEntered && !derivedItemIds.has(line.itemId)) {
        if (isExplicit(line, entries[line.itemId])) onChange(line.itemId, { quantity: null, unit });
      } else if (available !== null && available > 0) {
        onChange(line.itemId, { quantity: available, unit });
      }
      return;
    }
    if (onExclude && !onExclude(line.itemId)) setBlocked(line.itemId);
  };

  return (
    <section className="transport-zd-cargo" data-testid={readOnly ? `${tid}-view` : `${tid}-editor`}>
      {readOnly ? null : (
        <div className="transport-zd-cargo__intro">
          <Text type="secondary">{t('transport:supplierCargo.hintModes')}</Text>
        </div>
      )}

      <div className="transport-zd-trailer" data-testid={`${tid}-trailer`}>
        <div className="transport-zd-trailer__truck" aria-hidden="true">
          <div className="transport-zd-trailer__box">
            {weights.filter((item) => item.kg && item.kg > 0).map((item) => (
              <span
                key={item.line.itemId}
                className={`transport-zd-trailer__seg transport-zd-swatch-${swatchOf.get(item.line.itemId)}`}
                style={{ width: `${(item.kg! / scale) * 100}%` }}
                title={`${item.line.supplierOrderLabel} · ${t('transport:linePosition', { number: item.line.position })} · ${kg(item.kg!)}`}
              />
            ))}
          </div>
          <div className="transport-zd-trailer__cab" />
        </div>
        <div className="transport-zd-trailer__text">
          <Text strong className={totalKg > TRAILER_PAYLOAD_KG ? 'transport-zd-trailer__over' : undefined} data-testid={`${tid}-weight`}>
            {t('transport:supplierCargo.trailerWeight', { weight: kg(totalKg), limit: kg(TRAILER_PAYLOAD_KG) })}
          </Text>
          <Text type={totalKg > TRAILER_PAYLOAD_KG ? 'warning' : 'secondary'}>
            {totalKg > TRAILER_PAYLOAD_KG
              ? t('transport:supplierCargo.trailerOver', { weight: kg(totalKg - TRAILER_PAYLOAD_KG) })
              : t('transport:supplierCargo.trailerFree', { weight: kg(TRAILER_PAYLOAD_KG - totalKg) })}
            {withoutWeight > 0 ? ` ${t('transport:supplierCargo.trailerMissingWeight', { count: withoutWeight })}` : ''}
          </Text>
        </div>
      </div>

      {groups.map((group) => {
        const missing = group.lines.some((line) => states.get(line.itemId)?.ambiguous === true);
        const unit = group.lines[0]?.unit ?? null;
        const sameUnit = Boolean(unit) && group.lines.every((line) => line.unit === unit);
        const sum = (pick: (line: SupplierCargoLine) => number | null) => group.lines.reduce((total, line) => total + (pick(line) ?? 0), 0);
        return (
          <div key={group.supplierOrderId} className="transport-zd-cargo__group" data-testid={`${tid}-${group.supplierOrderId}`}>
            <header className="transport-zd-cargo__band">
              <span className="transport-zd-cargo__zd">{group.label}</span>
              {group.statusTag ?? null}
              <div className="transport-zd-cargo__band-text">
                {group.supplierName ? <span className="transport-zd-cargo__supplier">{group.supplierName}</span> : null}
                {group.loadingPlace ? (
                  <span className="transport-zd-cargo__place" title={group.loadingPlace}>{t('transport:supplierCargo.loadsAt', { place: group.loadingPlace.split('\n')[0] })}</span>
                ) : null}
              </div>
              {sameUnit ? (
                <span className="transport-zd-cargo__total" data-testid={`${tid}-total-${group.supplierOrderId}`}>
                  {t('transport:supplierCargo.totalOnTruck', {
                    carried: quantity(sum((line) => { const state = states.get(line.itemId); return state ? setValue(state.carried, unit) : 0; }), unit),
                    ordered: quantity(sum((line) => setValue(line.ordered, unit)), unit),
                  })}
                </span>
              ) : null}
              {onRemoveGroup ? (
                <Button
                  type="text"
                  size="small"
                  icon={<CloseOutlined />}
                  className="transport-zd-cargo__remove"
                  aria-label={t('transport:supplierOrders.remove', { number: group.label })}
                  onClick={() => onRemoveGroup(group.supplierOrderId)}
                />
              ) : null}
            </header>
            {missing ? (
              <Alert
                type="warning"
                showIcon
                data-testid={`${tid}-missing-warning`}
                message={t('transport:supplierCargo.missingQuantityWarning', {
                  number: group.label,
                  trucks: group.otherTruckLabels.join(', ') || t('transport:supplierCargo.otherTruck'),
                })}
              />
            ) : null}
            {group.contextUnavailable ? (
              <Alert type="info" showIcon message={t('transport:supplierCargo.otherTrucksUnknown')} />
            ) : null}
            {group.lines.length === 0 ? <Text type="secondary">—</Text> : null}
            <ul className="transport-zd-cargo__lines">
              {group.lines.map((line) => {
                const entry = entries[line.itemId];
                const explicit = isExplicit(line, entry);
                const current = entry ?? (line.savedExplicit ? { ...line.savedExplicit } : null);
                const editUnit: SupplierCargoUnit | null = (current?.unit ?? line.unit);
                const state = states.get(line.itemId);
                const carried = state ? setValue(state.carried, line.unit) : null;
                const value = explicit && current ? current.quantity : null;
                const orderedInUnit = setValue(line.ordered, line.unit);
                const otherInUnit = state?.onOtherTrucks ? setValue(state.onOtherTrucks, line.unit) : null;
                const available = orderedInUnit === null || otherInUnit === null
                  ? orderedInUnit
                  : Math.max(Math.round((orderedInUnit - otherInUnit) * 1000) / 1000, 0);
                const over = isOverOrdered(line, entry);
                const invalid = explicit && value !== null && !(value > 0);
                const mode: LineMode = partOpen.has(line.itemId) ? 'part'
                  : state?.notOnTruck ? 'none'
                    : explicit && value !== null && available !== null && editUnit === line.unit && value < available - TOLERANCE ? 'part'
                      : explicit && editUnit !== line.unit ? 'part'
                        : 'all';
                return (
                  <li key={line.itemId} className={`transport-zd-cargo__line is-${mode}`} data-testid={`${tid}-line-${line.itemId}`}>
                    <span className={`transport-zd-cargo__swatch transport-zd-swatch-${swatchOf.get(line.itemId)}`} aria-hidden="true" />
                    <span className="transport-zd-cargo__position" data-testid={`${tid}-position-${line.itemId}`}>
                      {line.position ?? '—'}
                    </span>
                    <div className="transport-zd-cargo__product">
                      <span className="transport-zd-cargo__name">{line.name}</span>
                      {line.dimensions ? <span className="transport-zd-cargo__dims">{line.dimensions}</span> : null}
                      <span className="transport-zd-cargo__meta">
                        <span>{t('transport:supplierCargo.metaOrdered', { amount: quantity(orderedInUnit, line.unit) })}</span>
                        <span data-testid={`${tid}-other-${line.itemId}`}>
                          {t('transport:supplierCargo.metaOther', { amount: group.loading ? '…' : quantity(otherInUnit, line.unit) })}
                        </span>
                        <span data-testid={`${tid}-unassigned-${line.itemId}`}>
                          {t('transport:supplierCargo.metaLeft', { amount: group.loading ? '…' : quantity(available, line.unit) })}
                        </span>
                      </span>
                    </div>
                    {readOnly ? <span /> : <div className="transport-zd-cargo__modes" role="radiogroup" aria-label={t('transport:supplierCargo.onThisTruckFor', { product: line.name })}>
                      {MODES.map((candidate) => (
                        <button
                          key={candidate}
                          type="button"
                          role="radio"
                          aria-checked={mode === candidate}
                          className={`transport-zd-cargo__mode transport-zd-cargo__mode--${candidate}${mode === candidate ? ' is-on' : ''}`}
                          data-testid={`${tid}-mode-${line.itemId}-${candidate}`}
                          onClick={() => setMode(group, line, candidate, available, carried)}
                        >
                          {t(`transport:supplierCargo.mode.${candidate}`)}
                        </button>
                      ))}
                    </div>}
                    <div className="transport-zd-cargo__amount">
                      {mode === 'part' && readOnly ? (
                        <span className="transport-zd-cargo__carried">{quantity(carried, line.unit)}</span>
                      ) : mode === 'part' ? (
                        <Space.Compact size="small">
                          <InputNumber
                            value={value ?? carried}
                            className={explicit && !derivedItemIds.has(line.itemId) ? undefined : 'transport-zd-cargo__input--default'}
                            status={invalid ? 'error' : over ? 'warning' : undefined}
                            aria-label={t('transport:supplierCargo.onThisTruckFor', { product: line.name })}
                            data-testid={`${tid}-input-${line.itemId}`}
                            onChange={(next) => {
                              if (!editUnit) return;
                              onChange(line.itemId, {
                                quantity: next === null || next === undefined ? null : Number(next),
                                unit: editUnit,
                              });
                            }}
                            style={{ width: 84 }}
                            {...POLISH_DECIMAL_INPUT_NUMBER_PROPS}
                          />
                          <Select<SupplierCargoUnit>
                            value={editUnit ?? undefined}
                            disabled={line.units.length <= 1}
                            aria-label={t('transport:supplierCargo.unitFor', { product: line.name })}
                            className="transport-zd-cargo__unit"
                            options={(line.units.length ? line.units : (editUnit ? [editUnit] : [])).map((unitOption) => ({
                              value: unitOption,
                              label: formatUnit(unitOption, i18n.language),
                            }))}
                            onChange={(nextUnit) => {
                              const converted = value !== null && editUnit
                                ? convertLineAmount(line, value, editUnit, nextUnit)
                                : null;
                              onChange(line.itemId, { quantity: explicit ? converted : null, unit: nextUnit });
                            }}
                          />
                        </Space.Compact>
                      ) : mode === 'none' ? (
                        <Text type="secondary" data-testid={`${tid}-not-on-truck-${line.itemId}`}>
                          {t('transport:supplierCargo.notOnThisTruck')}
                        </Text>
                      ) : (
                        <span className="transport-zd-cargo__carried">{quantity(carried, line.unit)}</span>
                      )}
                      {mode === 'part' && available !== null ? (
                        <Text type="secondary" className="transport-zd-cargo__of">{t('transport:supplierCargo.ofAvailable', { amount: quantity(available, line.unit) })}</Text>
                      ) : null}
                      {derivedItemIds.has(line.itemId) ? (
                        <Text type="secondary" className="transport-zd-cargo__default" data-testid={`${tid}-from-customers-${line.itemId}`}>
                          {t('transport:pairing.zdDefaultFromCustomers')}
                        </Text>
                      ) : null}
                    </div>
                    {blocked === line.itemId || state?.onNoTruck || invalid || over ? (
                      <div className="transport-zd-cargo__notes">
                        {blocked === line.itemId ? <Text type="warning">{t('transport:supplierCargo.cannotExclude')}</Text> : null}
                        {state?.onNoTruck ? (
                          <Text type="warning" data-testid={`${tid}-line-unassigned-${line.itemId}`}>
                            {singleFinalDot(t('transport:supplierCargo.lineUnassigned', {
                              product: line.position
                                ? `${line.name} (${t('transport:linePosition', { number: line.position })})`
                                : line.name,
                              remaining: quantity(state.unassigned ? setValue(state.unassigned, line.unit) : null, line.unit),
                            }))}
                          </Text>
                        ) : null}
                        {invalid ? <Text type="danger">{t('transport:supplierCargo.quantityPositive')}</Text> : null}
                        {over ? <Text type="warning">{t('transport:supplierCargo.overOrdered')}</Text> : null}
                      </div>
                    ) : null}
                  </li>
                );
              })}
            </ul>
          </div>
        );
      })}
    </section>
  );
};

export default TransportSupplierCargoEditor;
